"use client";

import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import QRCode from "qrcode";
import jsQR from "jsqr";
import {
  QrCode,
  Radio,
  Camera,
  Download,
  Copy,
  Check,
  AlertTriangle,
  Wifi,
  UserSquare2,
  Link2,
  Play,
  Pause,
  RotateCcw,
  Upload,
  Sparkles,
  ShieldCheck,
  VideoOff,
  FlaskConical,
  FileUp,
  FileCheck2,
  FileText,
  Image as ImageIcon,
  Info,
} from "lucide-react";

type EcLevel = "L" | "M" | "Q" | "H";
type GeneratorPreset = "url" | "wifi" | "vcard";
type ActiveTab = "generate" | "transmit" | "receive";
type StreamInputMode = "text" | "file";
type ImagePresetId = "compact" | "standard" | "hd" | "original";

const MAX_RAW_OPTICAL_BYTES = 1024 * 1024; // 1 MB raw binary limit (32x higher than old 32 KB)
const MAX_SOURCE_IMAGE_BYTES = 25 * 1024 * 1024; // Up to 25 MB camera photos compressed locally in browser

const IMAGE_PRESETS: Record<
  ImagePresetId,
  { label: string; maxEdge: number; quality: number; hint: string }
> = {
  compact: {
    label: "Kompakt (~20–35 QR)",
    maxEdge: 520,
    quality: 0.72,
    hint: "Ideal für schnellen Kamera-Scan (~15–28 KB, ~3–4 Sek.)",
  },
  standard: {
    label: "Standard (~45–80 QR)",
    maxEdge: 880,
    quality: 0.78,
    hint: "Gute Schärfe für Fotos & Screenshots (~35–65 KB)",
  },
  hd: {
    label: "HD (~100–180 QR)",
    maxEdge: 1280,
    quality: 0.82,
    hint: "Hohe Detailtreue (~80–150 KB, Vollbild empfohlen)",
  },
  original: {
    label: "Original (1:1 Bitgenau)",
    maxEdge: 0,
    quality: 1,
    hint: "Unveränderte Originaldatei ohne Neukompression (bis 1 MB)",
  },
};

const EC_INFO: Record<EcLevel, { label: string; recovery: string; hint: string }> = {
  L: { label: "L (7 %)", recovery: "~7 % Redundanz", hint: "Kleinste Matrix, nur für saubere Displays" },
  M: { label: "M (15 %)", recovery: "~15 % Redundanz", hint: "Empfohlener Standard für Print & Web" },
  Q: { label: "Q (25 %)", recovery: "~25 % Redundanz", hint: "Ideal bei leichtem Schmutz oder Schrägwinkel" },
  H: { label: "H (30 %)", recovery: "~30 % Redundanz", hint: "Maximale Reserve für raue Umgebungen" },
};

const COLOR_PRESETS = [
  { name: "Klassisch", fg: "#111827", bg: "#ffffff" },
  { name: "Smaragd", fg: "#064e3b", bg: "#ecfdf5" },
  { name: "Mitternacht", fg: "#1e1b4b", bg: "#f5f3ff" },
  { name: "Warn-Test (zu schwach)", fg: "#9ca3af", bg: "#f3f4f6" },
];

const STREAM_SAMPLES = [
  {
    label: "Server-Notiz (540 B)",
    content: `# Air-Gap Wartungsnotiz (Proxmox Node 02)
Host: pve-homelab-02.local (10.10.20.14)
VLAN-Tag: 20 (IoT-Isoliert, kein WAN-Uplink)
WireGuard Public Key: x9KpL2mQ8vR4tN7wZ1cB5yF3hJ6dG0sA8eU4iO2pM1k=
Backup-Prüfsumme (SHA-256): e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855
Hinweis: Übertragen per optischem QR-Frame-Stream ohne Netzwerkverbindung.`,
  },
  {
    label: "JSON-Konfiguration (790 B)",
    content: JSON.stringify(
      {
        version: "2.4.0",
        syncMode: "optical-qr-fountain",
        endpoints: [
          { name: "sensor-bridge", port: 8123, tls: true, retryMs: 1500 },
          { name: "local-index", port: 9090, tls: false, retryMs: 500 },
        ],
        featureFlags: {
          offlineCache: true,
          strictCrc16Check: true,
          telemetryUpload: false,
        },
        notes: "Jedes Frame ist einzeln nummeriert; der Empfänger kann mitten im Zyklus einsteigen.",
      },
      null,
      2
    ),
  },
];

const DEMO_CONF_FILE_TEXT = `[Interface]
# Optisch übertragene WireGuard-Konfiguration (Air-Gap)
PrivateKey = Y29uZmlnLWRlbW8tcHJpdmF0ZS1rZXktbG9rYWwtaW0tYnJvd3Nlcg==
Address = 10.20.0.4/24
DNS = 9.9.9.9

[Peer]
PublicKey = x9KpL2mQ8vR4tN7wZ1cB5yF3hJ6dG0sA8eU4iO2pM1k=
AllowedIPs = 10.20.0.0/24, 192.168.178.0/24
Endpoint = vpn.beispiel-homelab.de:51820
PersistentKeepalive = 25
`;

function escapeWifiField(val: string): string {
  return val.replace(/([\\;,:"])/g, "\\$1");
}

function hexToRgb(hex: string): [number, number, number] {
  const clean = hex.replace("#", "");
  const full =
    clean.length === 3
      ? clean
          .split("")
          .map((c) => c + c)
          .join("")
      : clean;
  const num = parseInt(full || "000000", 16);
  return [(num >> 16) & 255, (num >> 8) & 255, num & 255];
}

function relativeLuminance([r, g, b]: [number, number, number]): number {
  const toLinear = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

function computeContrast(fgHex: string, bgHex: string) {
  const lFg = relativeLuminance(hexToRgb(fgHex));
  const lBg = relativeLuminance(hexToRgb(bgHex));
  const lighter = Math.max(lFg, lBg);
  const darker = Math.min(lFg, lBg);
  const ratio = (lighter + 0.05) / (darker + 0.05);
  const isInverted = lFg > lBg;
  return { ratio, isInverted };
}

function crc16Ccitt(bytes: Uint8Array): string {
  let crc = 0xffff;
  for (let i = 0; i < bytes.length; i++) {
    crc ^= bytes[i] << 8;
    for (let b = 0; b < 8; b++) {
      crc = (crc & 0x8000) !== 0 ? ((crc << 1) ^ 0x1021) & 0xffff : (crc << 1) & 0xffff;
    }
  }
  return crc.toString(16).padStart(4, "0");
}

function bytesToBase64(bytes: Uint8Array): string {
  let bin = "";
  for (let i = 0; i < bytes.length; i++) {
    bin += String.fromCharCode(bytes[i]);
  }
  return btoa(bin);
}

function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) {
    out[i] = bin.charCodeAt(i);
  }
  return out;
}

function formatByteSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  mimeType: string,
  quality: number
): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), mimeType, quality);
  });
}

function isCompressibleRasterImage(mimeType: string, fileName: string): boolean {
  if (/^image\/(png|jpe?g|webp|bmp|gif|avif)$/i.test(mimeType)) return true;
  return /\.(png|jpe?g|webp|bmp|gif|avif)$/i.test(fileName);
}

async function compressImageInBrowser(
  sourceFile: File,
  maxEdge: number,
  quality: number
): Promise<{
  name: string;
  mimeType: string;
  bytes: Uint8Array;
  width: number;
  height: number;
  origWidth: number;
  origHeight: number;
}> {
  const objectUrl = URL.createObjectURL(sourceFile);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error("Bild konnte nicht dekodiert werden."));
      el.src = objectUrl;
    });

    const origWidth = img.naturalWidth || img.width || 1;
    const origHeight = img.naturalHeight || img.height || 1;
    const scale = maxEdge > 0 ? Math.min(1, maxEdge / Math.max(origWidth, origHeight)) : 1;
    const width = Math.max(1, Math.round(origWidth * scale));
    const height = Math.max(1, Math.round(origHeight * scale));

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas-2D-Kontext nicht verfügbar.");

    ctx.drawImage(img, 0, 0, width, height);

    // Prefer WebP for steep file-size savings; fall back to JPEG if browser lacks WebP encoder
    let blob = await canvasToBlob(canvas, "image/webp", quality);
    let ext = "webp";
    if (!blob || blob.type !== "image/webp") {
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);
      blob = await canvasToBlob(canvas, "image/jpeg", quality);
      ext = "jpg";
    }
    if (!blob) throw new Error("Bildkompression fehlgeschlagen.");

    const buf = await blob.arrayBuffer();
    const baseName = sourceFile.name.replace(/\.[^.]+$/, "") || "bild";
    return {
      name: `${baseName}-qr.${ext}`,
      mimeType: blob.type || (ext === "webp" ? "image/webp" : "image/jpeg"),
      bytes: new Uint8Array(buf),
      width,
      height,
      origWidth,
      origHeight,
    };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

async function generateDemoImageBytes(): Promise<{
  name: string;
  mimeType: string;
  bytes: Uint8Array;
  width: number;
  height: number;
}> {
  const width = 560;
  const height = 340;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d")!;

  const grad = ctx.createLinearGradient(0, 0, width, height);
  grad.addColorStop(0, "#052e16");
  grad.addColorStop(0.5, "#065f46");
  grad.addColorStop(1, "#0f172a");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, width, height);

  // Subtle grid
  ctx.strokeStyle = "rgba(255,255,255,0.08)";
  ctx.lineWidth = 1;
  for (let x = 0; x < width; x += 28) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, height);
    ctx.stroke();
  }
  for (let y = 0; y < height; y += 28) {
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(width, y);
    ctx.stroke();
  }

  // Glowing sun & geometric peaks
  ctx.fillStyle = "#34d399";
  ctx.beginPath();
  ctx.arc(430, 105, 48, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = "rgba(15, 23, 42, 0.85)";
  ctx.beginPath();
  ctx.moveTo(0, 340);
  ctx.lineTo(150, 175);
  ctx.lineTo(290, 275);
  ctx.lineTo(420, 155);
  ctx.lineTo(560, 250);
  ctx.lineTo(560, 340);
  ctx.closePath();
  ctx.fill();

  // Crisp caption badge
  ctx.fillStyle = "rgba(255,255,255,0.94)";
  ctx.fillRect(28, 28, 290, 66);
  ctx.fillStyle = "#064e3b";
  ctx.font = "bold 18px monospace";
  ctx.fillText("OPTICAL AIR-GAP PHOTO", 42, 56);
  ctx.fillStyle = "#334155";
  ctx.font = "13px monospace";
  ctx.fillText("100% lokal per QR-Stream gesendet", 42, 78);

  let blob = await canvasToBlob(canvas, "image/webp", 0.78);
  let ext = "webp";
  if (!blob || blob.type !== "image/webp") {
    blob = await canvasToBlob(canvas, "image/jpeg", 0.8);
    ext = "jpg";
  }
  const buf = await blob!.arrayBuffer();
  return {
    name: `airgap-testbild.${ext}`,
    mimeType: blob!.type || "image/webp",
    bytes: new Uint8Array(buf),
    width,
    height,
  };
}

interface EnvelopeHeader {
  k: "text" | "file";
  n?: string;
  t?: string;
}

interface ReconstructedPayload {
  kind: "text" | "file";
  text: string | null;
  fileName: string | null;
  mimeType: string;
  rawBytes: Uint8Array;
}

interface ParsedFrame {
  protocol: "LKQR1" | "LKQR2";
  sessionId: string;
  index: number;
  total: number;
  crcHex: string;
  chunkBytes: Uint8Array;
}

interface OpticalEnvelopeMeta {
  envelope: Uint8Array;
  totalBytes: number;
  totalChunks: number;
  chunkByteSize: number;
  crcHex: string;
  sessionId: string;
}

function packOpticalEnvelopeMeta(
  input:
    | { kind: "text"; text: string }
    | { kind: "file"; name: string; mimeType: string; bytes: Uint8Array },
  chunkByteSize: number
): OpticalEnvelopeMeta {
  const encoder = new TextEncoder();
  const payloadBytes =
    input.kind === "text" ? encoder.encode(input.text || " ") : input.bytes;

  const headerObj: EnvelopeHeader =
    input.kind === "file"
      ? { k: "file", n: input.name, t: input.mimeType || "application/octet-stream" }
      : { k: "text" };

  const headerBytes = encoder.encode(JSON.stringify(headerObj));
  const envelope = new Uint8Array(2 + headerBytes.length + payloadBytes.length);
  envelope[0] = (headerBytes.length >> 8) & 0xff;
  envelope[1] = headerBytes.length & 0xff;
  envelope.set(headerBytes, 2);
  envelope.set(payloadBytes, 2 + headerBytes.length);

  const crcHex = crc16Ccitt(envelope);
  const sessionId = `${crcHex}${envelope.length.toString(36)}`;
  const totalChunks = Math.max(1, Math.ceil(envelope.length / chunkByteSize));

  return {
    envelope,
    totalBytes: payloadBytes.length,
    totalChunks,
    chunkByteSize,
    crcHex,
    sessionId,
  };
}

function getOpticalFrameString(meta: OpticalEnvelopeMeta, index: number): string {
  const safeIdx = Math.max(0, Math.min(meta.totalChunks - 1, index));
  const slice = meta.envelope.subarray(
    safeIdx * meta.chunkByteSize,
    (safeIdx + 1) * meta.chunkByteSize
  );
  const b64 = bytesToBase64(slice);
  return `LKQR2|${meta.sessionId}|${safeIdx + 1}/${meta.totalChunks}|${meta.crcHex}|${b64}`;
}

function unpackOpticalEnvelope(
  merged: Uint8Array,
  protocol: "LKQR1" | "LKQR2"
): ReconstructedPayload {
  if (protocol === "LKQR1" || merged.length < 4) {
    return {
      kind: "text",
      text: new TextDecoder("utf-8").decode(merged),
      fileName: null,
      mimeType: "text/plain;charset=utf-8",
      rawBytes: merged,
    };
  }

  const headerLen = (merged[0] << 8) | merged[1];
  if (headerLen <= 0 || 2 + headerLen > merged.length) {
    return {
      kind: "text",
      text: new TextDecoder("utf-8").decode(merged),
      fileName: null,
      mimeType: "text/plain;charset=utf-8",
      rawBytes: merged,
    };
  }

  try {
    const headerJson = new TextDecoder("utf-8").decode(merged.subarray(2, 2 + headerLen));
    const header = JSON.parse(headerJson) as EnvelopeHeader;
    const rawBytes = merged.slice(2 + headerLen);

    if (header.k === "file") {
      const mimeType = header.t || "application/octet-stream";
      const fileName = header.n || "optischer-transfer.bin";
      const looksText =
        /^text\//i.test(mimeType) ||
        /\.(txt|md|json|conf|env|yaml|yml|toml|csv|svg|ts|js|py|sh|pub|pem)$/i.test(fileName);
      let preview: string | null = null;
      if (looksText && rawBytes.length <= 32_768) {
        try {
          preview = new TextDecoder("utf-8", { fatal: true }).decode(rawBytes);
        } catch {
          preview = null;
        }
      }
      return {
        kind: "file",
        text: preview,
        fileName,
        mimeType,
        rawBytes,
      };
    }

    return {
      kind: "text",
      text: new TextDecoder("utf-8").decode(rawBytes),
      fileName: null,
      mimeType: "text/plain;charset=utf-8",
      rawBytes,
    };
  } catch {
    return {
      kind: "text",
      text: new TextDecoder("utf-8").decode(merged),
      fileName: null,
      mimeType: "text/plain;charset=utf-8",
      rawBytes: merged,
    };
  }
}

function parseFrameString(raw: string): ParsedFrame | null {
  const isV2 = raw.startsWith("LKQR2|");
  const isV1 = raw.startsWith("LKQR1|");
  if (!isV2 && !isV1) return null;

  const parts = raw.split("|");
  if (parts.length !== 5) return null;
  const [, sessionId, posStr, crcHex, b64] = parts;
  const [idxStr, totalStr] = posStr.split("/");
  const index = Number(idxStr) - 1;
  const total = Number(totalStr);
  if (
    !sessionId ||
    !Number.isInteger(index) ||
    !Number.isInteger(total) ||
    index < 0 ||
    total < 1 ||
    index >= total
  ) {
    return null;
  }
  try {
    const chunkBytes = base64ToBytes(b64);
    return {
      protocol: isV2 ? "LKQR2" : "LKQR1",
      sessionId,
      index,
      total,
      crcHex,
      chunkBytes,
    };
  } catch {
    return null;
  }
}

export default function QrStudioPlayground() {
  const [activeTab, setActiveTab] = useState<ActiveTab>("generate");

  // --- Tab 1: Static Generator State ---
  const [preset, setPreset] = useState<GeneratorPreset>("url");
  const [urlInput, setUrlInput] = useState("https://lukiluki.me/garden");
  const [wifiSsid, setWifiSsid] = useState("Gaeste-WLAN");
  const [wifiPass, setWifiPass] = useState("SicheresHausnetz2026!");
  const [wifiEnc, setWifiEnc] = useState<"WPA" | "WEP" | "nopass">("WPA");
  const [vcardName, setVcardName] = useState("Lukas Kopischke");
  const [vcardRole, setVcardRole] = useState("Lead Software Engineer");
  const [vcardEmail, setVcardEmail] = useState("hallo@example.com");
  const [vcardUrl, setVcardUrl] = useState("https://lukiluki.me");

  const [ecLevel, setEcLevel] = useState<EcLevel>("M");
  const [fgColor, setFgColor] = useState("#111827");
  const [bgColor, setBgColor] = useState("#ffffff");
  const [svgMarkup, setSvgMarkup] = useState<string>("");
  const [pngDataUrl, setPngDataUrl] = useState<string>("");
  const [genError, setGenError] = useState<string | null>(null);
  const [copiedSvg, setCopiedSvg] = useState(false);

  const staticPayload = useMemo(() => {
    if (preset === "wifi") {
      const s = escapeWifiField(wifiSsid.trim() || "MeinWLAN");
      const p = wifiEnc === "nopass" ? "" : escapeWifiField(wifiPass);
      return `WIFI:T:${wifiEnc};S:${s};P:${p};;`;
    }
    if (preset === "vcard") {
      return [
        "BEGIN:VCARD",
        "VERSION:3.0",
        `FN:${vcardName.trim() || "Max Mustermann"}`,
        vcardRole.trim() ? `TITLE:${vcardRole.trim()}` : "",
        vcardEmail.trim() ? `EMAIL:${vcardEmail.trim()}` : "",
        vcardUrl.trim() ? `URL:${vcardUrl.trim()}` : "",
        "END:VCARD",
      ]
        .filter(Boolean)
        .join("\n");
    }
    return urlInput || "https://lukiluki.me";
  }, [preset, urlInput, wifiSsid, wifiPass, wifiEnc, vcardName, vcardRole, vcardEmail, vcardUrl]);

  const staticByteCount = useMemo(
    () => new TextEncoder().encode(staticPayload).length,
    [staticPayload]
  );

  const contrastStats = useMemo(() => computeContrast(fgColor, bgColor), [fgColor, bgColor]);

  useEffect(() => {
    let cancelled = false;
    async function renderStaticQr() {
      try {
        const [svg, png] = await Promise.all([
          QRCode.toString(staticPayload, {
            type: "svg",
            errorCorrectionLevel: ecLevel,
            margin: 2,
            color: { dark: fgColor, light: bgColor },
          }),
          QRCode.toDataURL(staticPayload, {
            errorCorrectionLevel: ecLevel,
            margin: 2,
            width: 1024,
            color: { dark: fgColor, light: bgColor },
          }),
        ]);
        if (!cancelled) {
          setSvgMarkup(svg);
          setPngDataUrl(png);
          setGenError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setGenError(
            err instanceof Error
              ? `Nutzlast zu groß für einen einzelnen QR-Code (${staticByteCount} Bytes). Nutze den Tab „Optischer Sender“ für längere Daten, Bilder oder Dateien!`
              : "QR-Code konnte nicht berechnet werden."
          );
        }
      }
    }
    renderStaticQr();
    return () => {
      cancelled = true;
    };
  }, [staticPayload, ecLevel, fgColor, bgColor, staticByteCount]);

  // --- Tab 2: Optical QR Stream Sender State (Text, Binary File up to 1 MB, & Smart Image Compression) ---
  const [streamMode, setStreamMode] = useState<StreamInputMode>("text");
  const [streamText, setStreamText] = useState(STREAM_SAMPLES[0].content);

  const [sourceImageFile, setSourceImageFile] = useState<File | null>(null);
  const [imagePreset, setImagePreset] = useState<ImagePresetId>("compact");

  const [txFile, setTxFile] = useState<{
    name: string;
    mimeType: string;
    bytes: Uint8Array;
    originalBytes?: number;
    dimensions?: string;
  } | null>(null);
  const [txFileError, setTxFileError] = useState<string | null>(null);

  const [chunkSize, setChunkSize] = useState<number>(320);
  const [fps, setFps] = useState<number>(8);
  const [isPlaying, setIsPlaying] = useState<boolean>(true);
  const [frameIndex, setFrameIndex] = useState<number>(0);
  const [streamFrameSvg, setStreamFrameSvg] = useState<string>("");

  // Preview URL for image currently loaded in Sender
  const txImagePreviewUrl = useMemo(() => {
    if (!txFile || !txFile.mimeType.startsWith("image/")) return null;
    const copy = new Uint8Array(txFile.bytes.length);
    copy.set(txFile.bytes);
    return URL.createObjectURL(new Blob([copy], { type: txFile.mimeType }));
  }, [txFile]);

  useEffect(() => {
    return () => {
      if (txImagePreviewUrl) URL.revokeObjectURL(txImagePreviewUrl);
    };
  }, [txImagePreviewUrl]);

  const encodedStream = useMemo(() => {
    if (streamMode === "file" && txFile) {
      return packOpticalEnvelopeMeta(
        {
          kind: "file",
          name: txFile.name,
          mimeType: txFile.mimeType,
          bytes: txFile.bytes,
        },
        chunkSize
      );
    }
    return packOpticalEnvelopeMeta({ kind: "text", text: streamText }, chunkSize);
  }, [streamMode, txFile, streamText, chunkSize]);

  const safeFrameIndex = frameIndex % Math.max(1, encodedStream.totalChunks);
  const estimatedCycleSec = (encodedStream.totalChunks / fps).toFixed(1);

  const autoTuneThroughput = (byteLength: number) => {
    if (byteLength > 22_000) {
      setChunkSize(950);
      setFps(8);
    } else if (byteLength > 6_000) {
      setChunkSize(600);
      setFps(8);
    } else {
      setChunkSize(320);
      setFps(4);
    }
  };

  const applyImagePresetToSource = async (file: File, nextPreset: ImagePresetId) => {
    setTxFileError(null);
    if (nextPreset === "original") {
      if (file.size > MAX_RAW_OPTICAL_BYTES) {
        setTxFileError(
          `Das unkomprimierte Originalbild ist ${formatByteSize(file.size)} groß (Limit für 1:1-Rohdaten: 1,00 MB). Wähle „Kompakt“, „Standard“ oder „HD“, um es automatisch für den QR-Transfer zu schrumpfen.`
        );
        return;
      }
      const buf = await file.arrayBuffer();
      const raw = new Uint8Array(buf);
      setImagePreset("original");
      setTxFile({
        name: file.name,
        mimeType: file.type || "image/png",
        bytes: raw,
        originalBytes: file.size,
      });
      autoTuneThroughput(raw.length);
      setFrameIndex(0);
      return;
    }

    try {
      const cfg = IMAGE_PRESETS[nextPreset];
      const compressed = await compressImageInBrowser(file, cfg.maxEdge, cfg.quality);
      setImagePreset(nextPreset);
      setTxFile({
        name: compressed.name,
        mimeType: compressed.mimeType,
        bytes: compressed.bytes,
        originalBytes: file.size,
        dimensions: `${compressed.width}×${compressed.height} px`,
      });
      autoTuneThroughput(compressed.bytes.length);
      setFrameIndex(0);
    } catch {
      setTxFileError("Das Bildformat konnte im Browser nicht verarbeitet werden.");
    }
  };

  const handleSelectTxFile = async (file?: File | null) => {
    if (!file) return;
    setTxFileError(null);

    if (isCompressibleRasterImage(file.type, file.name)) {
      if (file.size > MAX_SOURCE_IMAGE_BYTES) {
        setTxFileError(
          `Das Quellbild ist ${formatByteSize(file.size)} groß (Maximal 25 MB für die lokale Browser-Verkleinerung).`
        );
        return;
      }
      setSourceImageFile(file);
      setStreamMode("file");
      // Small images (< 30 KB) can stay original or compact; larger photos automatically shrink to ~20-35 QR codes!
      const initialPreset: ImagePresetId = file.size <= 28 * 1024 ? "original" : "compact";
      await applyImagePresetToSource(file, initialPreset);
      return;
    }

    setSourceImageFile(null);
    if (file.size > MAX_RAW_OPTICAL_BYTES) {
      setTxFileError(
        `Die Datei „${file.name}“ ist ${formatByteSize(file.size)} groß. Unkomprimierte Binärdateien sind bis ${formatByteSize(MAX_RAW_OPTICAL_BYTES)} (1 MB) freigeschaltet – für größere Archive nutze besser lokales WLAN (z. B. PairDrop/LocalSend).`
      );
      return;
    }
    const buf = await file.arrayBuffer();
    const bytes = new Uint8Array(buf);
    setTxFile({
      name: file.name,
      mimeType: file.type || "application/octet-stream",
      bytes,
    });
    autoTuneThroughput(bytes.length);
    setStreamMode("file");
    setFrameIndex(0);
  };

  const loadDemoImage = async () => {
    setTxFileError(null);
    setSourceImageFile(null);
    const demo = await generateDemoImageBytes();
    setTxFile({
      name: demo.name,
      mimeType: demo.mimeType,
      bytes: demo.bytes,
      dimensions: `${demo.width}×${demo.height} px`,
    });
    setChunkSize(600);
    setFps(8);
    setStreamMode("file");
    setFrameIndex(0);
  };

  const loadDemoConfFile = () => {
    const bytes = new TextEncoder().encode(DEMO_CONF_FILE_TEXT);
    setTxFileError(null);
    setSourceImageFile(null);
    setTxFile({
      name: "wireguard-peer.conf",
      mimeType: "text/plain;charset=utf-8",
      bytes,
    });
    setChunkSize(160);
    setFps(4);
    setStreamMode("file");
    setFrameIndex(0);
  };

  useEffect(() => {
    if (activeTab !== "transmit" || !isPlaying || encodedStream.totalChunks <= 1) return;
    const interval = setInterval(() => {
      setFrameIndex((prev) => (prev + 1) % encodedStream.totalChunks);
    }, Math.round(1000 / fps));
    return () => clearInterval(interval);
  }, [activeTab, isPlaying, fps, encodedStream.totalChunks]);

  useEffect(() => {
    let cancelled = false;
    const currentRawFrame = getOpticalFrameString(encodedStream, safeFrameIndex);
    // Use EC-Level L for dense frames (>= 600 B) so module pixels stay larger and sharper on displays
    const frameEc: EcLevel = encodedStream.chunkByteSize >= 600 ? "L" : "M";
    QRCode.toString(currentRawFrame, {
      type: "svg",
      errorCorrectionLevel: frameEc,
      margin: 2,
      color: { dark: "#09090b", light: "#ffffff" },
    })
      .then((svg) => {
        if (!cancelled) setStreamFrameSvg(svg);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [encodedStream, safeFrameIndex]);

  // --- Tab 3: Webcam & Loopback Receiver State ---
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const scanTimerRef = useRef<number | null>(null);
  const loopbackTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [isSimulating, setIsSimulating] = useState(false);
  const [singleResult, setSingleResult] = useState<string | null>(null);
  const [rxSession, setRxSession] = useState<{
    protocol: "LKQR1" | "LKQR2";
    sessionId: string;
    total: number;
    crcHex: string;
    firstCapturedIndex: number;
    lastCapturedIndex: number;
    chunks: Map<number, Uint8Array>;
    completedPayload: ReconstructedPayload | null;
    crcValid: boolean | null;
    droppedSimCount: number;
  } | null>(null);
  const [copiedRx, setCopiedRx] = useState(false);

  // Live Object URL when an image is reconstructed on the Receiver!
  const rxImagePreviewUrl = useMemo(() => {
    const payload = rxSession?.completedPayload;
    if (!payload || payload.kind !== "file" || !payload.mimeType.startsWith("image/")) {
      return null;
    }
    const copy = new Uint8Array(payload.rawBytes.length);
    copy.set(payload.rawBytes);
    return URL.createObjectURL(new Blob([copy], { type: payload.mimeType }));
  }, [rxSession?.completedPayload]);

  useEffect(() => {
    return () => {
      if (rxImagePreviewUrl) URL.revokeObjectURL(rxImagePreviewUrl);
    };
  }, [rxImagePreviewUrl]);

  const stopCamera = useCallback(() => {
    if (scanTimerRef.current) {
      cancelAnimationFrame(scanTimerRef.current);
      scanTimerRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraActive(false);
  }, []);

  const stopLoopback = useCallback(() => {
    if (loopbackTimerRef.current) {
      clearInterval(loopbackTimerRef.current);
      loopbackTimerRef.current = null;
    }
    setIsSimulating(false);
  }, []);

  const switchTab = useCallback(
    (nextTab: ActiveTab) => {
      if (nextTab !== "receive") {
        stopCamera();
        stopLoopback();
      }
      setActiveTab(nextTab);
    },
    [stopCamera, stopLoopback]
  );

  useEffect(() => {
    return () => {
      if (scanTimerRef.current) cancelAnimationFrame(scanTimerRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      if (loopbackTimerRef.current) clearInterval(loopbackTimerRef.current);
    };
  }, []);

  const ingestScannedString = useCallback(
    (raw: string, simulatedDrop = false) => {
      const parsed = parseFrameString(raw);
      if (!parsed) {
        setSingleResult(raw);
        return;
      }
      setSingleResult(null);
      setRxSession((prev) => {
        const base =
          prev && prev.sessionId === parsed.sessionId
            ? prev
            : {
                protocol: parsed.protocol,
                sessionId: parsed.sessionId,
                total: parsed.total,
                crcHex: parsed.crcHex,
                firstCapturedIndex: parsed.index,
                lastCapturedIndex: parsed.index,
                chunks: new Map<number, Uint8Array>(),
                completedPayload: null,
                crcValid: null,
                droppedSimCount: 0,
              };

        if (simulatedDrop) {
          return { ...base, droppedSimCount: base.droppedSimCount + 1 };
        }

        if (base.completedPayload !== null) return base;

        const nextChunks = new Map(base.chunks);
        nextChunks.set(parsed.index, parsed.chunkBytes);

        if (nextChunks.size === parsed.total) {
          if (loopbackTimerRef.current) {
            clearInterval(loopbackTimerRef.current);
            loopbackTimerRef.current = null;
          }
          setIsSimulating(false);
          stopCamera();

          let totalLen = 0;
          for (let i = 0; i < parsed.total; i++) {
            totalLen += nextChunks.get(i)?.length || 0;
          }
          const merged = new Uint8Array(totalLen);
          let offset = 0;
          for (let i = 0; i < parsed.total; i++) {
            const part = nextChunks.get(i)!;
            merged.set(part, offset);
            offset += part.length;
          }
          const actualCrc = crc16Ccitt(merged);
          const crcValid = actualCrc === parsed.crcHex;
          const completedPayload = unpackOpticalEnvelope(merged, parsed.protocol);
          return {
            ...base,
            lastCapturedIndex: parsed.index,
            chunks: nextChunks,
            completedPayload,
            crcValid,
          };
        }

        return {
          ...base,
          lastCapturedIndex: parsed.index,
          chunks: nextChunks,
        };
      });
    },
    [stopCamera]
  );

  const startCamera = async () => {
    stopLoopback();
    setCameraError(null);
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError(
        "Kamera-Zugriff wird in diesem Browser-Kontext nicht unterstützt (HTTPS oder Localhost erforderlich)."
      );
      return;
    }
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = mediaStream;
      setCameraActive(true);

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        await videoRef.current.play();
      }

      let lastScan = 0;
      const tick = (now: number) => {
        if (!streamRef.current) return;
        // ~28 Hz scan cadence so 8–12 FPS high-density streams are captured cleanly
        if (now - lastScan >= 35 && videoRef.current && canvasRef.current) {
          lastScan = now;
          const video = videoRef.current;
          if (video.readyState >= video.HAVE_ENOUGH_DATA && video.videoWidth > 0) {
            const canvas = canvasRef.current;
            const scale = Math.min(1, 800 / video.videoWidth);
            canvas.width = Math.floor(video.videoWidth * scale);
            canvas.height = Math.floor(video.videoHeight * scale);
            const ctx = canvas.getContext("2d", { willReadFrequently: true });
            if (ctx) {
              ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
              const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
              const code = jsQR(imgData.data, imgData.width, imgData.height, {
                inversionAttempts: "dontInvert",
              });
              if (code?.data) {
                ingestScannedString(code.data);
              }
            }
          }
        }
        scanTimerRef.current = requestAnimationFrame(tick);
      };
      scanTimerRef.current = requestAnimationFrame(tick);
    } catch {
      setCameraError(
        "Kamera-Freigabe wurde abgelehnt oder keine Kamera gefunden. Nutze alternativ den Bild-Upload oder den Loopback-Test."
      );
      setCameraActive(false);
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCameraError(null);
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const scale = Math.min(1, 1400 / Math.max(img.width, img.height));
      canvas.width = Math.max(1, Math.floor(img.width * scale));
      canvas.height = Math.max(1, Math.floor(img.height * scale));
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const decoded = jsQR(imgData.data, imgData.width, imgData.height, {
          inversionAttempts: "attemptBoth",
        });
        if (decoded?.data) {
          ingestScannedString(decoded.data);
        } else {
          setCameraError("Kein lesbarer QR-Code im hochgeladenen Bild erkannt.");
        }
      }
      URL.revokeObjectURL(objectUrl);
    };
    img.src = objectUrl;
    e.target.value = "";
  };

  const runLoopbackSimulation = () => {
    stopCamera();
    stopLoopback();
    setActiveTab("receive");
    setSingleResult(null);
    setCameraError(null);
    setRxSession(null);
    setIsSimulating(true);

    const total = encodedStream.totalChunks;
    // Start MID-STREAM (e.g. packet #14 of 28) to demonstrate automatic start & out-of-order reassembly!
    const midStart = total > 2 ? Math.floor(total / 2) : 0;
    let cursor = midStart;
    const stepMs = Math.max(25, Math.min(160, Math.round(3600 / Math.max(1, total))));

    loopbackTimerRef.current = setInterval(() => {
      const rawFrame = getOpticalFrameString(encodedStream, cursor);
      cursor = (cursor + 1) % total;
      const dropThisFrame = total > 2 && Math.random() < 0.14;
      ingestScannedString(rawFrame, dropThisFrame);
    }, stepMs);
  };

  const downloadReconstructedFile = () => {
    const payload = rxSession?.completedPayload;
    if (!payload) return;
    const copy = new Uint8Array(payload.rawBytes.length);
    copy.set(payload.rawBytes);
    const blob = new Blob([copy], { type: payload.mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = payload.fileName || "optischer-transfer.bin";
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadSvg = () => {
    if (!svgMarkup) return;
    const blob = new Blob([svgMarkup], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `qr-code-${preset}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadPng = () => {
    if (!pngDataUrl) return;
    const a = document.createElement("a");
    a.href = pngDataUrl;
    a.download = `qr-code-${preset}.png`;
    a.click();
  };

  const copySvg = async () => {
    if (!svgMarkup) return;
    await navigator.clipboard?.writeText(svgMarkup);
    setCopiedSvg(true);
    setTimeout(() => setCopiedSvg(false), 1800);
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Top Mode Switcher */}
      <div
        role="tablist"
        aria-label="QR-Studio Modi"
        className="grid grid-cols-1 gap-2 sm:grid-cols-3 rounded-2xl border border-neutral-200/80 bg-neutral-100/70 p-1.5 dark:border-neutral-800 dark:bg-neutral-900/70"
      >
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "generate"}
          onClick={() => switchTab("generate")}
          className={`flex min-h-[46px] items-center justify-center gap-2 rounded-xl px-4 py-2 text-xs sm:text-sm font-bold transition-all cursor-pointer ${
            activeTab === "generate"
              ? "bg-white text-emerald-700 shadow-xs dark:bg-neutral-800 dark:text-emerald-300"
              : "text-neutral-600 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white"
          }`}
        >
          <QrCode className="h-4 w-4 shrink-0" />
          <span>1. Statischer QR-Code</span>
        </button>

        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "transmit"}
          onClick={() => switchTab("transmit")}
          className={`flex min-h-[46px] items-center justify-center gap-2 rounded-xl px-4 py-2 text-xs sm:text-sm font-bold transition-all cursor-pointer ${
            activeTab === "transmit"
              ? "bg-white text-emerald-700 shadow-xs dark:bg-neutral-800 dark:text-emerald-300"
              : "text-neutral-600 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white"
          }`}
        >
          <Radio className="h-4 w-4 shrink-0" />
          <span>2. Optischer Sender (Text, Bild &amp; Datei)</span>
        </button>

        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "receive"}
          onClick={() => switchTab("receive")}
          className={`flex min-h-[46px] items-center justify-center gap-2 rounded-xl px-4 py-2 text-xs sm:text-sm font-bold transition-all cursor-pointer ${
            activeTab === "receive"
              ? "bg-white text-emerald-700 shadow-xs dark:bg-neutral-800 dark:text-emerald-300"
              : "text-neutral-600 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white"
          }`}
        >
          <Camera className="h-4 w-4 shrink-0" />
          <span>3. Webcam-Empfänger</span>
        </button>
      </div>

      {/* TAB 1: STATIC QR GENERATOR */}
      {activeTab === "generate" && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="flex flex-col gap-4 lg:col-span-7">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mr-1">
                Vorlage:
              </span>
              {[
                { id: "url" as const, label: "Link / Text", icon: Link2 },
                { id: "wifi" as const, label: "WLAN-Zugang", icon: Wifi },
                { id: "vcard" as const, label: "Visitenkarte (vCard)", icon: UserSquare2 },
              ].map((item) => {
                const Icon = item.icon;
                const active = preset === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setPreset(item.id)}
                    className={`inline-flex min-h-[40px] items-center gap-1.5 rounded-xl border px-3.5 py-1.5 text-xs font-semibold transition-all cursor-pointer ${
                      active
                        ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-800 dark:text-emerald-300"
                        : "border-neutral-200/80 bg-white text-neutral-700 hover:bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-300"
                    }`}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </div>

            {preset === "url" && (
              <div>
                <label
                  htmlFor="qr-url-input"
                  className="mb-1.5 block text-xs font-semibold text-neutral-700 dark:text-neutral-300"
                >
                  Ziel-URL oder beliebiger Text (direkt in die Matrix codiert – kein Redirect-Server)
                </label>
                <textarea
                  id="qr-url-input"
                  rows={3}
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  placeholder="https://deine-seite.de"
                  className="w-full rounded-xl border border-neutral-200 bg-white p-3.5 font-mono text-sm text-neutral-900 focus:border-emerald-500 focus:outline-hidden dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-100"
                />
              </div>
            )}

            {preset === "wifi" && (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="qr-wifi-ssid"
                    className="mb-1 block text-xs font-semibold text-neutral-700 dark:text-neutral-300"
                  >
                    Netzwerkname (SSID)
                  </label>
                  <input
                    id="qr-wifi-ssid"
                    type="text"
                    value={wifiSsid}
                    onChange={(e) => setWifiSsid(e.target.value)}
                    className="min-h-[42px] w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 focus:border-emerald-500 focus:outline-hidden dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-100"
                  />
                </div>
                <div>
                  <label
                    htmlFor="qr-wifi-enc"
                    className="mb-1 block text-xs font-semibold text-neutral-700 dark:text-neutral-300"
                  >
                    Verschlüsselung
                  </label>
                  <select
                    id="qr-wifi-enc"
                    value={wifiEnc}
                    onChange={(e) => setWifiEnc(e.target.value as "WPA" | "WEP" | "nopass")}
                    className="min-h-[42px] w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 focus:border-emerald-500 focus:outline-hidden dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-100"
                  >
                    <option value="WPA">WPA / WPA2 / WPA3</option>
                    <option value="WEP">WEP (veraltet)</option>
                    <option value="nopass">Offenes WLAN (kein Passwort)</option>
                  </select>
                </div>
                {wifiEnc !== "nopass" && (
                  <div className="sm:col-span-2">
                    <label
                      htmlFor="qr-wifi-pass"
                      className="mb-1 block text-xs font-semibold text-neutral-700 dark:text-neutral-300"
                    >
                      WLAN-Passwort (verlässt niemals deinen Browser)
                    </label>
                    <input
                      id="qr-wifi-pass"
                      type="text"
                      value={wifiPass}
                      onChange={(e) => setWifiPass(e.target.value)}
                      className="min-h-[42px] w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 font-mono text-sm text-neutral-900 focus:border-emerald-500 focus:outline-hidden dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-100"
                    />
                  </div>
                )}
              </div>
            )}

            {preset === "vcard" && (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="qr-vc-name"
                    className="mb-1 block text-xs font-semibold text-neutral-700 dark:text-neutral-300"
                  >
                    Vollständiger Name
                  </label>
                  <input
                    id="qr-vc-name"
                    type="text"
                    value={vcardName}
                    onChange={(e) => setVcardName(e.target.value)}
                    className="min-h-[42px] w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-100"
                  />
                </div>
                <div>
                  <label
                    htmlFor="qr-vc-role"
                    className="mb-1 block text-xs font-semibold text-neutral-700 dark:text-neutral-300"
                  >
                    Rolle / Unternehmen
                  </label>
                  <input
                    id="qr-vc-role"
                    type="text"
                    value={vcardRole}
                    onChange={(e) => setVcardRole(e.target.value)}
                    className="min-h-[42px] w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-100"
                  />
                </div>
                <div>
                  <label
                    htmlFor="qr-vc-mail"
                    className="mb-1 block text-xs font-semibold text-neutral-700 dark:text-neutral-300"
                  >
                    E-Mail
                  </label>
                  <input
                    id="qr-vc-mail"
                    type="email"
                    value={vcardEmail}
                    onChange={(e) => setVcardEmail(e.target.value)}
                    className="min-h-[42px] w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-100"
                  />
                </div>
                <div>
                  <label
                    htmlFor="qr-vc-url"
                    className="mb-1 block text-xs font-semibold text-neutral-700 dark:text-neutral-300"
                  >
                    Webseite
                  </label>
                  <input
                    id="qr-vc-url"
                    type="url"
                    value={vcardUrl}
                    onChange={(e) => setVcardUrl(e.target.value)}
                    className="min-h-[42px] w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-100"
                  />
                </div>
              </div>
            )}

            {/* Reed-Solomon & Colors */}
            <div className="grid grid-cols-1 gap-4 rounded-xl border border-neutral-200/80 bg-neutral-50/70 p-3.5 dark:border-neutral-800 dark:bg-neutral-900/50 sm:grid-cols-2">
              <div>
                <div className="mb-1.5 flex items-center justify-between text-xs font-semibold text-neutral-700 dark:text-neutral-300">
                  <span>Reed-Solomon Fehlerkorrektur</span>
                  <span className="font-mono text-[11px] text-emerald-700 dark:text-emerald-400">
                    {EC_INFO[ecLevel].recovery}
                  </span>
                </div>
                <div className="grid grid-cols-4 gap-1.5">
                  {(["L", "M", "Q", "H"] as EcLevel[]).map((lvl) => (
                    <button
                      key={lvl}
                      type="button"
                      onClick={() => setEcLevel(lvl)}
                      className={`min-h-[38px] rounded-lg border font-mono text-xs font-bold transition-all cursor-pointer ${
                        ecLevel === lvl
                          ? "border-emerald-500 bg-emerald-600 text-white"
                          : "border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-100 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200"
                      }`}
                    >
                      {lvl}
                    </button>
                  ))}
                </div>
                <p className="mt-1.5 text-[11px] text-neutral-500 dark:text-neutral-400">
                  {EC_INFO[ecLevel].hint}
                </p>
              </div>

              <div>
                <div className="mb-1.5 flex items-center justify-between text-xs font-semibold text-neutral-700 dark:text-neutral-300">
                  <span>Farben &amp; Kontrast-Prüfung</span>
                  <span
                    className={`font-mono text-[11px] font-bold ${
                      contrastStats.ratio < 4.5 || contrastStats.isInverted
                        ? "text-amber-600 dark:text-amber-400"
                        : "text-emerald-700 dark:text-emerald-400"
                    }`}
                  >
                    {contrastStats.ratio.toFixed(1)}:1
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <label className="flex flex-1 items-center gap-2 rounded-lg border border-neutral-200 bg-white px-2.5 py-1.5 text-xs dark:border-neutral-700 dark:bg-neutral-800">
                    <input
                      type="color"
                      aria-label="Punktfarbe (Vordergrund)"
                      value={fgColor}
                      onChange={(e) => setFgColor(e.target.value)}
                      className="h-5 w-5 cursor-pointer rounded border-0 bg-transparent p-0"
                    />
                    <span className="font-mono text-[11px]">{fgColor}</span>
                  </label>
                  <label className="flex flex-1 items-center gap-2 rounded-lg border border-neutral-200 bg-white px-2.5 py-1.5 text-xs dark:border-neutral-700 dark:bg-neutral-800">
                    <input
                      type="color"
                      aria-label="Hintergrundfarbe"
                      value={bgColor}
                      onChange={(e) => setBgColor(e.target.value)}
                      className="h-5 w-5 cursor-pointer rounded border-0 bg-transparent p-0"
                    />
                    <span className="font-mono text-[11px]">{bgColor}</span>
                  </label>
                </div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {COLOR_PRESETS.map((cp) => (
                    <button
                      key={cp.name}
                      type="button"
                      onClick={() => {
                        setFgColor(cp.fg);
                        setBgColor(cp.bg);
                      }}
                      className="rounded-md border border-neutral-200/80 bg-white px-2 py-0.5 text-[10px] font-medium text-neutral-600 hover:bg-neutral-100 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-300 cursor-pointer"
                    >
                      {cp.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {(contrastStats.ratio < 4.5 || contrastStats.isInverted) && (
              <div className="flex items-start gap-2.5 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-900 dark:text-amber-200">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
                <div>
                  <p className="font-semibold">Eingeschränkte Scan-Zuverlässigkeit erkannt:</p>
                  <p className="mt-0.5">
                    {contrastStats.isInverted
                      ? "Helle Module auf dunklem Grund (Invertierung) werden von vielen älteren Scannern und Paketdienst-Terminals nicht erkannt. Wähle dunkle Module auf hellem Grund."
                      : `Das Kontrastverhältnis (${contrastStats.ratio.toFixed(1)}:1) liegt unter dem Richtwert von 4,5:1 – bei Dämmerlicht drohen Lesefehler.`}
                  </p>
                </div>
              </div>
            )}

            {genError && (
              <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-3 text-xs text-red-800 dark:text-red-200">
                {genError}
              </div>
            )}
          </div>

          {/* Right Preview & Download Column */}
          <div className="flex flex-col items-center justify-between rounded-2xl border border-neutral-200/80 bg-neutral-50/80 p-5 dark:border-neutral-800 dark:bg-neutral-900/60 lg:col-span-5">
            <div className="flex w-full items-center justify-between text-xs text-neutral-500 dark:text-neutral-400">
              <span className="inline-flex items-center gap-1 font-medium text-emerald-700 dark:text-emerald-400">
                <ShieldCheck className="h-3.5 w-3.5" />
                Statisch · 0 Server-Redirects
              </span>
              <span className="font-mono">{staticByteCount} Bytes</span>
            </div>

            <div className="my-5 flex min-h-[250px] w-full max-w-[270px] items-center justify-center rounded-2xl border border-neutral-200/80 bg-white p-4 shadow-xs dark:border-neutral-700">
              {svgMarkup ? (
                <div
                  className="h-full w-full [&>svg]:h-auto [&>svg]:w-full"
                  dangerouslySetInnerHTML={{ __html: svgMarkup }}
                />
              ) : (
                <span className="text-xs text-neutral-400">Vorschau wird berechnet…</span>
              )}
            </div>

            <div className="flex w-full flex-col gap-2">
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={downloadSvg}
                  disabled={!svgMarkup}
                  className="inline-flex min-h-[42px] items-center justify-center gap-1.5 rounded-xl bg-emerald-600 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-emerald-700 disabled:opacity-50 cursor-pointer"
                >
                  <Download className="h-3.5 w-3.5" />
                  <span>SVG (Vektor)</span>
                </button>
                <button
                  type="button"
                  onClick={downloadPng}
                  disabled={!pngDataUrl}
                  className="inline-flex min-h-[42px] items-center justify-center gap-1.5 rounded-xl border border-neutral-300 bg-white px-3 py-2 text-xs font-semibold text-neutral-800 transition-colors hover:bg-neutral-100 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100 dark:hover:bg-neutral-700 cursor-pointer"
                >
                  <Download className="h-3.5 w-3.5" />
                  <span>PNG (1024 px)</span>
                </button>
              </div>
              <button
                type="button"
                onClick={copySvg}
                disabled={!svgMarkup}
                className="inline-flex min-h-[40px] items-center justify-center gap-1.5 rounded-xl border border-neutral-200/80 bg-transparent px-3 py-1.5 text-xs font-medium text-neutral-600 hover:bg-neutral-100 dark:border-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-800 cursor-pointer"
              >
                {copiedSvg ? (
                  <>
                    <Check className="h-3.5 w-3.5 text-emerald-500" />
                    <span>SVG-Markup kopiert!</span>
                  </>
                ) : (
                  <>
                    <Copy className="h-3.5 w-3.5" />
                    <span>SVG-Code in Zwischenablage kopieren</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: OPTICAL AIR-GAP STREAM TRANSMITTER (TEXT, SMART IMAGES UP TO 25 MB, & RAW FILES UP TO 1 MB) */}
      {activeTab === "transmit" && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="flex flex-col gap-4 lg:col-span-7">
            {/* Input Mode Switcher: Text vs Image/File Upload */}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="inline-flex rounded-xl border border-neutral-200 bg-neutral-100/80 p-1 dark:border-neutral-800 dark:bg-neutral-900">
                <button
                  type="button"
                  onClick={() => {
                    setStreamMode("text");
                    setFrameIndex(0);
                  }}
                  className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all cursor-pointer ${
                    streamMode === "text"
                      ? "bg-white text-neutral-900 shadow-2xs dark:bg-neutral-800 dark:text-white"
                      : "text-neutral-600 dark:text-neutral-400"
                  }`}
                >
                  <FileText className="h-3.5 w-3.5" />
                  <span>Text / Code</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setStreamMode("file");
                    if (!txFile) void loadDemoImage();
                    setFrameIndex(0);
                  }}
                  className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all cursor-pointer ${
                    streamMode === "file"
                      ? "bg-white text-emerald-700 shadow-2xs dark:bg-neutral-800 dark:text-emerald-300"
                      : "text-neutral-600 dark:text-neutral-400"
                  }`}
                >
                  <ImageIcon className="h-3.5 w-3.5" />
                  <span>Bild oder Datei senden (bis 1 MB / Fotos auto-komprimiert)</span>
                </button>
              </div>

              {streamMode === "text" ? (
                <div className="flex flex-wrap gap-1.5">
                  {STREAM_SAMPLES.map((s) => (
                    <button
                      key={s.label}
                      type="button"
                      onClick={() => {
                        setStreamText(s.content);
                        setChunkSize(160);
                        setFps(4);
                        setFrameIndex(0);
                      }}
                      className="inline-flex items-center gap-1 rounded-lg border border-neutral-200 bg-white px-2.5 py-1 text-xs font-medium text-neutral-700 hover:bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-300 cursor-pointer"
                    >
                      <Sparkles className="h-3 w-3 text-emerald-500" />
                      {s.label}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  <button
                    type="button"
                    onClick={() => void loadDemoImage()}
                    className="inline-flex items-center gap-1 rounded-lg border border-neutral-200 bg-white px-2.5 py-1 text-xs font-medium text-neutral-700 hover:bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-300 cursor-pointer"
                  >
                    <ImageIcon className="h-3 w-3 text-emerald-500" />
                    Demo-Bild laden
                  </button>
                  <button
                    type="button"
                    onClick={loadDemoConfFile}
                    className="inline-flex items-center gap-1 rounded-lg border border-neutral-200 bg-white px-2.5 py-1 text-xs font-medium text-neutral-700 hover:bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-300 cursor-pointer"
                  >
                    <Sparkles className="h-3 w-3 text-emerald-500" />
                    wireguard.conf
                  </button>
                </div>
              )}
            </div>

            {streamMode === "text" ? (
              <textarea
                rows={7}
                aria-label="Zu übertragender Text für den optischen QR-Stream"
                value={streamText}
                onChange={(e) => {
                  setStreamText(e.target.value);
                  setFrameIndex(0);
                }}
                placeholder="Füge hier längeren Text, JSON, SSH-Keys oder Notizen ein…"
                className="w-full rounded-xl border border-neutral-200 bg-white p-3.5 font-mono text-xs sm:text-sm text-neutral-900 focus:border-emerald-500 focus:outline-hidden dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-100"
              />
            ) : (
              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  void handleSelectTxFile(e.dataTransfer.files?.[0]);
                }}
                className="flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-emerald-500/35 bg-emerald-500/[0.03] p-5 text-center dark:border-emerald-500/25"
              >
                {txImagePreviewUrl ? (
                  /* eslint-disable-next-line @next/next/no-img-element */
                  <img
                    src={txImagePreviewUrl}
                    alt={txFile?.name || "Bildvorschau"}
                    className="max-h-36 rounded-xl border border-neutral-200 object-contain shadow-xs dark:border-neutral-700"
                  />
                ) : (
                  <FileUp className="h-8 w-8 text-emerald-600 dark:text-emerald-400" />
                )}

                {txFile ? (
                  <div className="space-y-1">
                    <div className="font-mono text-sm font-bold text-neutral-900 dark:text-white">
                      {txFile.name}
                    </div>
                    <div className="text-xs text-neutral-600 dark:text-neutral-300">
                      {txFile.originalBytes && txFile.originalBytes !== txFile.bytes.length ? (
                        <>
                          <span className="line-through text-neutral-400 mr-1.5">
                            {formatByteSize(txFile.originalBytes)}
                          </span>
                          <strong className="text-emerald-700 dark:text-emerald-300">
                            → {formatByteSize(txFile.bytes.length)}
                          </strong>
                          {txFile.dimensions ? ` (${txFile.dimensions})` : ""}
                        </>
                      ) : (
                        <span>
                          {formatByteSize(txFile.bytes.length)}
                          {txFile.dimensions ? ` · ${txFile.dimensions}` : ""}
                        </span>
                      )}{" "}
                      · <strong>{encodedStream.totalChunks} QR-Pakete</strong> (~{estimatedCycleSec} Sek./Umlauf)
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-neutral-600 dark:text-neutral-400">
                    Ziehe ein Foto/Screenshot (bis 25 MB – wird automatisch als schlankes WebP auf ~20–60 QR-Codes komprimiert) oder eine beliebige Datei bis 1 MB hierher
                  </p>
                )}

                {/* Smart Image Compression Quality Selector (shown whenever user uploaded a raster image) */}
                {sourceImageFile && (
                  <div className="w-full max-w-xl rounded-xl border border-neutral-200/80 bg-white/90 p-2.5 text-left dark:border-neutral-800 dark:bg-neutral-900/90">
                    <div className="mb-1.5 flex items-center justify-between text-[11px] font-semibold text-neutral-700 dark:text-neutral-300">
                      <span>Bild-Optimierung für schnellen QR-Transfer:</span>
                      <span className="font-normal text-neutral-500">
                        {IMAGE_PRESETS[imagePreset].hint}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4">
                      {(Object.keys(IMAGE_PRESETS) as ImagePresetId[]).map((presetKey) => {
                        const active = imagePreset === presetKey;
                        const disabledOrig =
                          presetKey === "original" && sourceImageFile.size > MAX_RAW_OPTICAL_BYTES;
                        return (
                          <button
                            key={presetKey}
                            type="button"
                            disabled={disabledOrig}
                            onClick={() => void applyImagePresetToSource(sourceImageFile, presetKey)}
                            className={`rounded-lg border px-2 py-1.5 text-[11px] font-semibold transition-all cursor-pointer disabled:cursor-not-allowed disabled:opacity-40 ${
                              active
                                ? "border-emerald-500 bg-emerald-600 text-white"
                                : "border-neutral-200 bg-neutral-50 text-neutral-700 hover:bg-neutral-100 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200"
                            }`}
                          >
                            {IMAGE_PRESETS[presetKey].label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <label className="inline-flex min-h-[40px] cursor-pointer items-center gap-2 rounded-xl bg-neutral-900 px-4 py-2 text-xs font-semibold text-white hover:bg-neutral-800 dark:bg-white dark:text-neutral-900">
                  <Upload className="h-3.5 w-3.5" />
                  <span>Eigenes Bild oder Datei auswählen</span>
                  <input
                    type="file"
                    onChange={(e) => void handleSelectTxFile(e.target.files?.[0])}
                    className="sr-only"
                  />
                </label>

                {txFileError && (
                  <p className="mt-1 text-xs font-medium text-amber-700 dark:text-amber-300">
                    {txFileError}
                  </p>
                )}
              </div>
            )}

            {/* Stream Parameters */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="rounded-xl border border-neutral-200/80 bg-neutral-50/70 p-3 dark:border-neutral-800 dark:bg-neutral-900/50">
                <label
                  htmlFor="qr-fps-select"
                  className="mb-1.5 block text-xs font-semibold text-neutral-700 dark:text-neutral-300"
                >
                  Bildrate (Frames pro Sekunde)
                </label>
                <select
                  id="qr-fps-select"
                  value={fps}
                  onChange={(e) => setFps(Number(e.target.value))}
                  className="min-h-[40px] w-full rounded-lg border border-neutral-200 bg-white px-2.5 py-1.5 text-xs font-medium dark:border-neutral-800 dark:bg-neutral-900"
                >
                  <option value={2}>2 FPS (sehr sicher für ältere Kameras)</option>
                  <option value={4}>4 FPS (klassisch)</option>
                  <option value={8}>8 FPS (schnell – moderne Smartphones)</option>
                  <option value={12}>12 FPS (Turbo – 60-Hz-Kamera &amp; Vollbild empfohlen)</option>
                </select>
              </div>

              <div className="rounded-xl border border-neutral-200/80 bg-neutral-50/70 p-3 dark:border-neutral-800 dark:bg-neutral-900/50">
                <label
                  htmlFor="qr-chunk-select"
                  className="mb-1.5 block text-xs font-semibold text-neutral-700 dark:text-neutral-300"
                >
                  Datenmenge pro QR-Code
                </label>
                <select
                  id="qr-chunk-select"
                  value={chunkSize}
                  onChange={(e) => {
                    setChunkSize(Number(e.target.value));
                    setFrameIndex(0);
                  }}
                  className="min-h-[40px] w-full rounded-lg border border-neutral-200 bg-white px-2.5 py-1.5 text-xs font-medium dark:border-neutral-800 dark:bg-neutral-900"
                >
                  <option value={160}>160 Bytes (grobe Matrix – aus Distanz scanbar)</option>
                  <option value={320}>320 Bytes (ausgewogen)</option>
                  <option value={600}>600 Bytes (schnell – ideal für Laptop/Monitor)</option>
                  <option value={950}>950 Bytes (Maximum – z. B. ~25 QR-Codes für Kompakt-Foto)</option>
                </select>
              </div>
            </div>

            {/* Automatic Start Detection Explainer Box */}
            <div className="flex items-start gap-2.5 rounded-xl border border-neutral-200/80 bg-neutral-50/80 p-3 text-xs text-neutral-600 dark:border-neutral-800 dark:bg-neutral-900/50 dark:text-neutral-400">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
              <div>
                <strong className="text-neutral-900 dark:text-neutral-200">
                  Automatische Start-Erkennung:
                </strong>{" "}
                Jedes einzelne QR-Bild trägt seinen Paket-Index (z.&nbsp;B.{" "}
                <code className="font-mono">
                  {Math.min(4, encodedStream.totalChunks)}/{encodedStream.totalChunks}
                </code>
                ) und die Prüfsumme im Header. Du musst <strong>nicht</strong> auf Paket 1 warten –
                halte die Kamera jederzeit mitten in die Endlosschleife!
              </div>
            </div>

            {/* 1-Click Loopback Demo */}
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-emerald-500/25 bg-emerald-500/5 p-3.5 text-xs">
              <div className="space-y-0.5">
                <p className="font-semibold text-neutral-900 dark:text-neutral-100">
                  Direkt ohne Zweitgerät ausprobieren:
                </p>
                <p className="text-neutral-600 dark:text-neutral-400">
                  Startet absichtlich mitten im Zyklus, simuliert verpasste Frames und rekonstruiert Bild/Datei im Empfänger.
                </p>
              </div>
              <button
                type="button"
                onClick={runLoopbackSimulation}
                className="inline-flex min-h-[42px] items-center gap-1.5 rounded-xl bg-emerald-600 px-4 py-2 text-xs font-semibold text-white hover:bg-emerald-700 cursor-pointer"
              >
                <FlaskConical className="h-3.5 w-3.5" />
                <span>
                  {streamMode === "file"
                    ? "Bild-/Datei-Transfer simulieren"
                    : "Text-Transfer simulieren"}
                </span>
              </button>
            </div>
          </div>

          {/* Animated Stream Display */}
          <div className="flex flex-col items-center justify-between rounded-2xl border border-neutral-200/80 bg-neutral-50/80 p-5 dark:border-neutral-800 dark:bg-neutral-900/60 lg:col-span-5">
            <div className="flex w-full items-center justify-between font-mono text-xs">
              <span className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500/15 px-2.5 py-0.5 font-bold text-emerald-700 dark:text-emerald-300">
                <span>
                  Paket {safeFrameIndex + 1} / {encodedStream.totalChunks}
                </span>
                {safeFrameIndex === 0 && (
                  <span className="rounded bg-emerald-600 px-1 py-0.2 text-[9px] text-white">
                    ZYKLUS-START
                  </span>
                )}
              </span>
              <span className="text-neutral-500 dark:text-neutral-400">
                {formatByteSize(encodedStream.totalBytes)} · CRC {encodedStream.crcHex}
              </span>
            </div>

            <div className="my-4 flex min-h-[280px] w-full max-w-[330px] items-center justify-center rounded-2xl border-2 border-neutral-900/10 bg-white p-3.5 shadow-xs">
              {streamFrameSvg ? (
                <div
                  className="h-full w-full [&>svg]:h-auto [&>svg]:w-full"
                  dangerouslySetInnerHTML={{ __html: streamFrameSvg }}
                />
              ) : null}
            </div>

            <div className="flex w-full flex-col gap-2.5">
              {/* Progress Dots for <= 80 packages, smooth scrubber slider for larger transfers */}
              {encodedStream.totalChunks <= 80 ? (
                <div className="flex max-h-16 flex-wrap justify-center gap-1 overflow-y-auto py-0.5">
                  {Array.from({ length: encodedStream.totalChunks }).map((_, idx) => (
                    <button
                      key={idx}
                      type="button"
                      aria-label={`Springe zu Paket ${idx + 1}`}
                      onClick={() => {
                        setIsPlaying(false);
                        setFrameIndex(idx);
                      }}
                      className={`h-2.5 w-2.5 rounded-xs transition-all cursor-pointer ${
                        idx === safeFrameIndex
                          ? "scale-125 bg-emerald-500"
                          : idx === 0
                            ? "bg-emerald-500/40 hover:bg-emerald-500/70"
                            : "bg-neutral-300 hover:bg-neutral-400 dark:bg-neutral-700"
                      }`}
                    />
                  ))}
                </div>
              ) : (
                <div className="px-1">
                  <input
                    type="range"
                    aria-label="Paket-Position im Stream"
                    min={0}
                    max={encodedStream.totalChunks - 1}
                    value={safeFrameIndex}
                    onChange={(e) => {
                      setIsPlaying(false);
                      setFrameIndex(Number(e.target.value));
                    }}
                    className="w-full accent-emerald-600 cursor-pointer"
                  />
                </div>
              )}

              <button
                type="button"
                onClick={() => setIsPlaying((p) => !p)}
                className="inline-flex min-h-[42px] w-full items-center justify-center gap-1.5 rounded-xl border border-neutral-300 bg-white px-3 py-2 text-xs font-semibold text-neutral-800 hover:bg-neutral-100 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100 cursor-pointer"
              >
                {isPlaying ? (
                  <>
                    <Pause className="h-3.5 w-3.5" />
                    <span>Endlosschleife pausieren</span>
                  </>
                ) : (
                  <>
                    <Play className="h-3.5 w-3.5" />
                    <span>Endlosschleife fortsetzen</span>
                  </>
                )}
              </button>
              <p className="text-center text-[11px] text-neutral-500 dark:text-neutral-400">
                Umlaufdauer: ~{estimatedCycleSec} Sek. · Tipp: Für dichte 950-Byte-Codes oben rechts „Vollbild“ nutzen.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* TAB 3: WEBCAM & IMAGE/FILE/TEXT RECEIVER */}
      {activeTab === "receive" && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="flex flex-col gap-3 lg:col-span-6">
            <div className="flex flex-wrap gap-2">
              {!cameraActive ? (
                <button
                  type="button"
                  onClick={startCamera}
                  className="inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-xs font-semibold text-white hover:bg-emerald-700 cursor-pointer"
                >
                  <Camera className="h-4 w-4" />
                  <span>Kamera starten (Live-Scan)</span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={stopCamera}
                  className="inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-red-600 px-4 py-2 text-xs font-semibold text-white hover:bg-red-700 cursor-pointer"
                >
                  <VideoOff className="h-4 w-4" />
                  <span>Kamera stoppen</span>
                </button>
              )}

              <label className="inline-flex min-h-[44px] cursor-pointer items-center gap-2 rounded-xl border border-neutral-300 bg-white px-3.5 py-2 text-xs font-semibold text-neutral-800 hover:bg-neutral-100 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200">
                <Upload className="h-4 w-4" />
                <span>QR-Bilddatei prüfen</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="sr-only"
                />
              </label>

              <button
                type="button"
                onClick={() => {
                  stopLoopback();
                  setRxSession(null);
                  setSingleResult(null);
                  setCameraError(null);
                }}
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-xl border border-neutral-200 px-3 py-2 text-xs font-medium text-neutral-600 hover:bg-neutral-100 dark:border-neutral-800 dark:text-neutral-400 dark:hover:bg-neutral-800 cursor-pointer"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                <span>Zurücksetzen</span>
              </button>
            </div>

            <div className="relative flex min-h-[260px] items-center justify-center overflow-hidden rounded-2xl border border-neutral-200 bg-neutral-950 text-neutral-300 dark:border-neutral-800">
              <video
                ref={videoRef}
                playsInline
                muted
                className={`h-full max-h-[320px] w-full object-cover ${cameraActive ? "block" : "hidden"}`}
              />
              <canvas ref={canvasRef} className="hidden" />
              {!cameraActive && (
                <div className="flex flex-col items-center gap-2 p-6 text-center text-xs text-neutral-400">
                  {isSimulating ? (
                    <>
                      <Radio className="h-8 w-8 animate-pulse text-emerald-400" />
                      <p className="font-semibold text-emerald-300">
                        Optischer Loopback-Stream läuft…
                      </p>
                      <p>
                        Einstieg erfolgte mitten im Zyklus – Pakete werden anhand ihres Header-Index
                        sortiert.
                      </p>
                    </>
                  ) : rxSession?.completedPayload ? (
                    <>
                      <ShieldCheck className="h-9 w-9 text-emerald-400" />
                      <p className="font-semibold text-emerald-300">
                        Empfang vollständig abgeschlossen!
                      </p>
                      <p>Kamera wurde nach erfolgreicher CRC-16-Prüfung automatisch gestoppt.</p>
                    </>
                  ) : (
                    <>
                      <Camera className="h-8 w-8 text-neutral-600" />
                      <p>
                        Kamera ist ausgeschaltet. Alle Video-Frames werden ausschließlich lokal im
                        Arbeitsspeicher ausgewertet – der Einstieg ist bei jedem beliebigen Paket
                        möglich.
                      </p>
                    </>
                  )}
                </div>
              )}
            </div>

            {cameraError && (
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-800 dark:text-amber-200">
                {cameraError}
              </div>
            )}
          </div>

          {/* Reassembly / Decoded Image, File, or Text Output */}
          <div className="flex flex-col justify-between rounded-2xl border border-neutral-200/80 bg-neutral-50/80 p-5 dark:border-neutral-800 dark:bg-neutral-900/60 lg:col-span-6">
            <div>
              <div className="mb-3 flex items-center justify-between">
                <div className="font-heading text-sm font-bold text-neutral-900 dark:text-white">
                  Empfangs- &amp; Paket-Status
                </div>
                {rxSession && (
                  <span className="font-mono text-xs font-semibold text-emerald-700 dark:text-emerald-300">
                    {rxSession.chunks.size} / {rxSession.total} Pakete (
                    {Math.round((rxSession.chunks.size / rxSession.total) * 100)} %)
                  </span>
                )}
              </div>

              {rxSession && (
                <div className="mb-4 space-y-2.5">
                  {/* Proof of Mid-Stream Start Detection */}
                  <div className="rounded-lg border border-neutral-200/80 bg-white/80 px-3 py-1.5 font-mono text-[11px] text-neutral-600 dark:border-neutral-800 dark:bg-neutral-950/60 dark:text-neutral-400">
                    Erstes erfasstes Paket:{" "}
                    <strong className="text-neutral-900 dark:text-white">
                      #{rxSession.firstCapturedIndex + 1} von {rxSession.total}
                    </strong>{" "}
                    · Zuletzt gelesen: <strong>#{rxSession.lastCapturedIndex + 1}</strong>
                    {rxSession.total > rxSession.chunks.size && (
                      <>
                        {" "}
                        · Noch offen: <strong>{rxSession.total - rxSession.chunks.size}</strong>
                      </>
                    )}
                  </div>

                  {/* Chunk Matrix (numbered boxes up to 80, compact 64-block heatmap above 80) */}
                  {rxSession.total <= 80 ? (
                    <div className="flex max-h-28 flex-wrap gap-1.5 overflow-y-auto py-0.5">
                      {Array.from({ length: rxSession.total }).map((_, idx) => {
                        const got = rxSession.chunks.has(idx);
                        const isJustScanned =
                          idx === rxSession.lastCapturedIndex && !rxSession.completedPayload;
                        return (
                          <div
                            key={idx}
                            className={`flex h-7 w-7 items-center justify-center rounded-md font-mono text-[11px] font-bold transition-all ${
                              got
                                ? isJustScanned
                                  ? "scale-110 bg-emerald-400 text-neutral-950 ring-2 ring-emerald-500"
                                  : "bg-emerald-500 text-white shadow-2xs"
                                : "border border-neutral-300 bg-white text-neutral-400 dark:border-neutral-700 dark:bg-neutral-900"
                            }`}
                          >
                            {idx + 1}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      <div className="grid grid-cols-16 gap-1">
                        {Array.from({ length: 64 }).map((_, bucketIdx) => {
                          const start = Math.floor((bucketIdx * rxSession.total) / 64);
                          const end = Math.max(
                            start + 1,
                            Math.floor(((bucketIdx + 1) * rxSession.total) / 64)
                          );
                          let count = 0;
                          for (let k = start; k < end; k++) {
                            if (rxSession.chunks.has(k)) count++;
                          }
                          const ratio = count / (end - start);
                          return (
                            <div
                              key={bucketIdx}
                              title={`Pakete ${start + 1}–${end}: ${Math.round(ratio * 100)} %`}
                              className={`h-3 rounded-xs transition-colors ${
                                ratio === 1
                                  ? "bg-emerald-500"
                                  : ratio > 0
                                    ? "bg-emerald-400/50"
                                    : "bg-neutral-200 dark:bg-neutral-800"
                              }`}
                            />
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {rxSession.droppedSimCount > 0 && (
                    <p className="font-mono text-[11px] text-neutral-500 dark:text-neutral-400">
                      Übersprungene Kamera-Frames im nächsten Umlauf aufgeholt:{" "}
                      {rxSession.droppedSimCount}
                    </p>
                  )}

                  {rxSession.crcValid !== null && (
                    <div
                      className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-semibold ${
                        rxSession.crcValid
                          ? "bg-emerald-500/15 text-emerald-800 dark:text-emerald-300"
                          : "bg-red-500/15 text-red-700 dark:text-red-300"
                      }`}
                    >
                      <ShieldCheck className="h-4 w-4" />
                      {rxSession.crcValid
                        ? `CRC-16 Prüfsumme (${rxSession.crcHex}) bestätigt – bitgenau ab Paket #1 rekonstruiert!`
                        : "CRC-Prüfsummenfehler!"}
                    </div>
                  )}
                </div>
              )}

              {/* Reconstructed Output: Live Image Preview + File Download OR Text Box */}
              {rxSession?.completedPayload?.kind === "file" ? (
                <div className="space-y-3 rounded-2xl border border-emerald-500/40 bg-white p-4 shadow-xs dark:bg-neutral-950">
                  {rxImagePreviewUrl && (
                    <div className="flex justify-center overflow-hidden rounded-xl border border-neutral-200 bg-neutral-100/70 p-2 dark:border-neutral-800 dark:bg-neutral-900">
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        src={rxImagePreviewUrl}
                        alt={rxSession.completedPayload.fileName || "Empfangenes Bild"}
                        className="max-h-56 rounded-lg object-contain"
                      />
                    </div>
                  )}

                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
                        <FileCheck2 className="h-5 w-5" />
                      </div>
                      <div className="min-w-0">
                        <div className="truncate font-mono text-sm font-bold text-neutral-900 dark:text-white">
                          {rxSession.completedPayload.fileName}
                        </div>
                        <div className="text-xs text-neutral-500 dark:text-neutral-400">
                          {formatByteSize(rxSession.completedPayload.rawBytes.length)} ·{" "}
                          {rxSession.completedPayload.mimeType}
                        </div>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={downloadReconstructedFile}
                      className="inline-flex min-h-[42px] shrink-0 items-center gap-1.5 rounded-xl bg-emerald-600 px-4 py-2 text-xs font-bold text-white hover:bg-emerald-700 cursor-pointer"
                    >
                      <Download className="h-4 w-4" />
                      <span>
                        {rxImagePreviewUrl ? "Bild herunterladen" : "Datei herunterladen"}
                      </span>
                    </button>
                  </div>

                  {rxSession.completedPayload.text && (
                    <div>
                      <div className="mb-1 text-[11px] font-semibold text-neutral-500">
                        Datei-Inhaltsvorschau:
                      </div>
                      <textarea
                        readOnly
                        rows={5}
                        aria-label="Vorschau der empfangenen Datei"
                        value={rxSession.completedPayload.text}
                        className="w-full rounded-xl border border-neutral-200 bg-neutral-50 p-2.5 font-mono text-xs text-neutral-900 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-100"
                      />
                    </div>
                  )}
                </div>
              ) : rxSession?.completedPayload?.text ? (
                <textarea
                  readOnly
                  rows={7}
                  aria-label="Rekonstruierte Nutzdaten"
                  value={rxSession.completedPayload.text}
                  className="w-full rounded-xl border border-emerald-500/40 bg-white p-3 font-mono text-xs text-neutral-900 dark:bg-neutral-950 dark:text-neutral-100"
                />
              ) : singleResult ? (
                <textarea
                  readOnly
                  rows={7}
                  aria-label="Gescannter Einzel-QR-Inhalt"
                  value={singleResult}
                  className="w-full rounded-xl border border-emerald-500/40 bg-white p-3 font-mono text-xs text-neutral-900 dark:bg-neutral-950 dark:text-neutral-100"
                />
              ) : (
                <div className="flex min-h-[160px] items-center justify-center rounded-xl border border-dashed border-neutral-300 p-4 text-center text-xs text-neutral-500 dark:border-neutral-700 dark:text-neutral-400">
                  {rxSession
                    ? "Sammle noch fehlende Pakete aus der laufenden Endlosschleife…"
                    : "Noch keine Daten empfangen. Starte die Kamera, lade ein QR-Bild hoch oder teste im Tab 2 den Bild-/Datei-Loopback."}
                </div>
              )}
            </div>

            {(rxSession?.completedPayload?.text || singleResult) &&
              rxSession?.completedPayload?.kind !== "file" && (
                <button
                  type="button"
                  onClick={async () => {
                    const out = rxSession?.completedPayload?.text || singleResult || "";
                    await navigator.clipboard?.writeText(out);
                    setCopiedRx(true);
                    setTimeout(() => setCopiedRx(false), 1800);
                  }}
                  className="mt-3 inline-flex min-h-[42px] w-full items-center justify-center gap-1.5 rounded-xl bg-neutral-900 px-3 py-2 text-xs font-semibold text-white hover:bg-neutral-800 dark:bg-white dark:text-neutral-900 dark:hover:bg-neutral-200 cursor-pointer"
                >
                  {copiedRx ? (
                    <>
                      <Check className="h-3.5 w-3.5 text-emerald-400 dark:text-emerald-600" />
                      <span>Empfangenen Text kopiert!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="h-3.5 w-3.5" />
                      <span>Empfangenen Text kopieren</span>
                    </>
                  )}
                </button>
              )}
          </div>
        </div>
      )}
    </div>
  );
}
